import {
  allowedHistoryPeriods,
  fromDateToMonth,
  getDisplayablePeriod,
  getNextPeriodOf,
  getPeriodByPeriodId,
  getPreviousPeriodOf,
  historyPeriodToDateTuple,
  PredefinedPeriodIds,
  timestampToMoment
} from '@weenat/client/dist/core/history'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { useIntl } from '@weenat/wintl'
import useDisclosure from 'app/src/hooks/useDisclosure'
import Text from 'app/src/kit/Text'
import { isEqual } from 'lodash-es'
import isNil from 'lodash-es/isNil'
import moment from 'moment-timezone'
import { ReactNode, useEffect, useState } from 'react'
import { styled } from 'styled-components'
import Button from './Button'
import WeenatCalendar from './calendar/WeenatCalendar'
import DelimitedFlex from './DelimitedFlex'
import { INPUT_HEIGHT } from './fields/constants'
import { Dropdown, DropdownContent, DropdownTrigger } from './fields/Select/SelectDropdown'
import Icons from './Icons'
import { disabled } from './mixins/disabled'
import { focusOutline } from './mixins/focus'
import { interactiveBackgroundHover } from './mixins/interactive'
import PeriodItem from './periodSelector/PeriodItem'
import { SuggestionList } from './periodSelector/SuggestionList'
import TextTooltip from './tooltips/TextTooltip'

const WIDGET_INPUT_HEIGHT = 30

interface NavigationIconHolderProps {
  $isDisabled: boolean
  $isCompact: boolean
}

const NavigationIconHolder = styled(Flex)<NavigationIconHolderProps>`
  height: ${(p) => (p.$isCompact ? WIDGET_INPUT_HEIGHT : INPUT_HEIGHT)}px;
  width: ${(p) => (p.$isCompact ? WIDGET_INPUT_HEIGHT : INPUT_HEIGHT)}px;

  align-items: center;
  justify-content: center;

  overflow: hidden;

  ${(p) => p.$isDisabled && disabled}

  ${interactiveBackgroundHover}
`

const MainContainer = styled(Flex)<{
  $isDisabled?: boolean
  $isFocused: boolean
  $isCompact: boolean
}>`
  background-color: ${(p) => p.theme.colors.grayscale[50]};

  height: ${(p) => (p.$isCompact ? WIDGET_INPUT_HEIGHT : INPUT_HEIGHT)}px;

  position: relative;

  align-items: center;

  border-radius: ${(p) => p.theme.spacings.md}px;
  border: 1px solid ${(p) => p.theme.colors.grayscale[300]};

  overflow: hidden;

  ${(p) => p.$isFocused && focusOutline()}

  ${(p) => p.$isDisabled && disabled}
`

const PeriodContainer = styled(Box)`
  padding: ${(p) =>
    `${p.theme.spacings[400]}px ${p.theme.spacings[200]}px ${p.theme.spacings[400]}px ${p.theme.spacings[200]}px`};
  flex: 1;
  cursor: pointer;

  ${interactiveBackgroundHover}
`

export interface PeriodSelectorProps {
  since: number
  before: number
  currentPeriodId: PredefinedPeriodIds
  maxDate?: Date
  minDate?: Date
  onChange: (period: { since: number; before: number }, periodId?: PredefinedPeriodIds) => void
  onNext?: () => void
  onPrevious?: () => void
  timeStep: TimeSteps
  timezone: string
  unauthorizedPeriodIds?: PredefinedPeriodIds[]
  isDisabled?: boolean
  variant?: 'default' | 'widget'
  /** Rendered to the right of the widget period selector (only in variant='widget') */
  linkButton?: ReactNode
}

const EMPTY_UNAUTHORIZED_PERIOD_IDS: PredefinedPeriodIds[] = []

/**
 * Pressable opening a modal with a calendar to select a period
 */
const PeriodSelector: FC<PeriodSelectorProps> = ({
  since: initialSince,
  before: initialBefore,
  currentPeriodId: initialCurrentPeriodId,
  maxDate,
  minDate,
  isDisabled = false,
  onChange,
  onNext,
  onPrevious,
  timeStep,
  timezone,
  unauthorizedPeriodIds: unauthorizedPeriodIdsFromProps = EMPTY_UNAUTHORIZED_PERIOD_IDS,
  variant = 'default',
  linkButton
}) => {
  const { t, formatDateRange } = useIntl()

  const unauthorizedPeriodIds = [...unauthorizedPeriodIdsFromProps, 'custom']

  const {
    toggle: togglePeriodIdDropdown,
    close: closePeriodIdDropdown,
    open: openPeriodIdDropdown,
    isOpen: isPeriodIdDropdownOpen
  } = useDisclosure()

  const [currentPeriod, setCurrentPeriod] = useState<{ since: number; before: number }>({
    since: initialSince,
    before: initialBefore
  })

  const [currentPeriodId, setCurrentPeriodId] =
    useState<PredefinedPeriodIds>(initialCurrentPeriodId)

  const { since, before } = currentPeriod
  const { since: displayableSince, before: displayableBefore } = getDisplayablePeriod(
    currentPeriod,
    timezone
  )

  const periodContext = {
    period: currentPeriod,
    timezone,
    timeStep,
    periodId: currentPeriodId
  }

  const previousPeriod = getPreviousPeriodOf(periodContext)
  const nextPeriod = getNextPeriodOf(periodContext)

  /** If the next period starts before the maximum date (so if we have data, even for part of the next period) then we allow for navigation */
  const hasNext = !isNil(maxDate) ? moment.unix(nextPeriod.since).isBefore(maxDate) : true
  /** If the previous period ends before the min date (so if we have data, even for part of the previous period) then we allow for navigation */
  const hasPrevious = !isNil(minDate) ? moment.unix(previousPeriod.before).isAfter(minDate) : true

  const sinceAsDate = timestampToMoment(currentPeriod.since, timezone).toDate()
  const initialMonth = fromDateToMonth(sinceAsDate)

  const predefinedPeriod = allowedHistoryPeriods
    .filter((periodId) => !unauthorizedPeriodIds.includes(periodId))
    .map((id) => {
      const period = getPeriodByPeriodId({ periodId: id, relativeTo: moment(), timezone })

      const displayed = getDisplayablePeriod(period, timezone)

      return (
        <PeriodItem
          key={id}
          onPress={() => {
            setCurrentPeriod(period)
            setCurrentPeriodId('custom')
          }}
          forceActivity={currentPeriodId === id || isEqual(period, currentPeriod)}
          label={t(`periods.${id}`, { capitalize: true })}
          sublabel={formatDateRange(displayed.since, displayed.before, {
            dateStyle: 'medium',
            hour12: false,
            timeZone: timezone
          })}
        />
      )
    })

  // overrides the local state if the props change
  useEffect(() => {
    setCurrentPeriod({ since: initialSince, before: initialBefore })
  }, [initialSince, initialBefore])

  useEffect(() => {
    setCurrentPeriodId(initialCurrentPeriodId)
  }, [initialCurrentPeriodId])

  return (
    <MainContainer
      $isDisabled={isDisabled}
      $isFocused={isPeriodIdDropdownOpen}
      $isCompact={variant === 'widget'}
    >
      {/* Clickable element */}
      <Dropdown
        isVisible={isPeriodIdDropdownOpen}
        onVisibleChange={(becomeVisible) => {
          if (becomeVisible) {
            openPeriodIdDropdown()
          } else {
            closePeriodIdDropdown()
            setCurrentPeriod({ since: initialSince, before: initialBefore })
          }
        }}
      >
        <DropdownTrigger>
          <PeriodContainer
            className='period-selector-container'
            onClick={() => togglePeriodIdDropdown()}
          >
            <Flex $alignItems='center' $gap={'300'}>
              <Icons.CalendarDateRange $size='lg' />

              <Flex
                $flex={1}
                $alignItems='center'
                $justifyContent='space-between'
                $minWidth={variant === 'widget' ? undefined : 196}
              >
                {isDisabled ? null : (
                  <Text $textAlign='left' $fontWeight='medium' $lineHeight='md'>
                    {formatDateRange(displayableSince, displayableBefore, {
                      dateStyle: 'medium',
                      timeZone: timezone
                    })}
                  </Text>
                )}
              </Flex>

              {isPeriodIdDropdownOpen ? (
                <Icons.ChevronUp $size='lg' />
              ) : (
                <Icons.ChevronDown $size='lg' />
              )}
            </Flex>
          </PeriodContainer>
        </DropdownTrigger>
        <DropdownContent width={640}>
          <Flex $flexDirection='column'>
            <DelimitedFlex $flex={1} $alignItems='stretch'>
              <DelimitedFlex
                $flexDirection='column'
                $flex={1}
                $isVertical
                $isDelimitedOnRight
                $p='lg'
                $maxHeight={400}
                $overflowY='auto'
              >
                <Flex $flexDirection='column' $minHeight='fit-content'>
                  <Text $fontSize='sm' $fontWeight='medium' $color='grayscale.700'>
                    {t('periods.proposal', { capitalize: true })}
                  </Text>
                  <Flex $flexDirection='column' $gap='sm' $my='sm'>
                    <SuggestionList
                      since={since}
                      before={before}
                      onChange={(period) => {
                        setCurrentPeriod(period)
                        setCurrentPeriodId('custom')
                      }}
                    />
                  </Flex>
                </Flex>
                <Flex $flexDirection='column' $minHeight='fit-content'>
                  <Text $fontSize='sm' $fontWeight='medium' $color='grayscale.700'>
                    {t('periods.label_plural', { capitalize: true })}
                  </Text>
                  <Flex $flexDirection='column' $gap='sm' $my='sm'>
                    {predefinedPeriod}
                  </Flex>
                </Flex>
                <Flex
                  $backgroundColor='plans.plus.200'
                  $p='md'
                  $gap='md'
                  $justifyContent='space-between'
                  $mx='sm'
                  $borderRadius='md'
                  $minHeight='fit-content'
                >
                  <Icons.Lightbulb $size='md' $color='plans.plus.800' />
                  <Text $color='plans.plus.800' $fontWeight='medium' $flexed>
                    {t('periods.tips')}
                  </Text>
                </Flex>
              </DelimitedFlex>
              <Flex $pl='md' $p='md' $width={352}>
                <WeenatCalendar
                  variant='period'
                  value={historyPeriodToDateTuple(currentPeriod, timezone)}
                  initialMonth={initialMonth}
                  maxDate={maxDate}
                  onChange={(value) => {
                    if (Array.isArray(value)) {
                      setCurrentPeriod({
                        since: value[0].getTime() / 1000,
                        before: value[1].getTime() / 1000
                      })
                      setCurrentPeriodId('custom')
                    }
                  }}
                  onCancel={closePeriodIdDropdown}
                  timezone={timezone}
                  autoChange
                  hidePeriodHeader
                />
              </Flex>
            </DelimitedFlex>
            <Flex $justifyContent='space-between' $alignItems='center' $p='md'>
              <Text $color='grayscale.700'>
                {t('periods.custom_with_values', {
                  since: moment(currentPeriod.since * 1000).format('DD MMM'),
                  before: moment(currentPeriod.before * 1000).format('DD MMM YYYY')
                })}
              </Text>
              <Flex $justifyContent='center' $alignItems='center' $gap='md'>
                <Button
                  importance='sd'
                  variant='compact'
                  onPress={() => {
                    setCurrentPeriod({ since: initialSince, before: initialBefore })
                    closePeriodIdDropdown()
                  }}
                >
                  {t('actions.cancel', { capitalize: true })}
                </Button>
                <Button
                  variant='small'
                  onPress={() => {
                    onChange(currentPeriod, currentPeriodId)
                    closePeriodIdDropdown()
                  }}
                >
                  {t('actions.confirm', { capitalize: true })}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </DropdownContent>
      </Dropdown>
      {!isNil(onNext) || !isNil(onPrevious) ? (
        <Flex $borderLeftWidth={1} $borderLeftStyle='solid' $borderLeftColor={'grayscale.100'}>
          {!isNil(onPrevious) ? (
            <TextTooltip content={t('history.previous_period', { capitalize: true })}>
              <NavigationIconHolder
                $isCompact={variant === 'widget'}
                $isDisabled={!hasPrevious || isDisabled}
                onClick={() => {
                  if (hasPrevious) {
                    onPrevious()
                    onChange(previousPeriod, 'custom')
                  }
                }}
              >
                <Icons.ArrowRight $rotate={180} $size='lg' />
              </NavigationIconHolder>
            </TextTooltip>
          ) : null}
          {!isNil(onNext) ? (
            <TextTooltip content={t('history.next_period', { capitalize: true })}>
              <NavigationIconHolder
                $isCompact={variant === 'widget'}
                $isDisabled={!hasNext || isDisabled}
                onClick={() => {
                  if (hasNext) {
                    onNext()
                    onChange(nextPeriod, 'custom')
                  }
                }}
              >
                <Icons.ArrowRight $size='lg' />
              </NavigationIconHolder>
            </TextTooltip>
          ) : null}
        </Flex>
      ) : null}
      {!isNil(linkButton) ? linkButton : null}
    </MainContainer>
  )
}

export default PeriodSelector
